import {
  BarChart2,
  Calendar,
  Camera,
  Clock,
  Info,
  Loader2,
  Mail,
  Save,
  Settings,
  TrendingUp,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import type { CurrencyApi } from "@/api/models/currency";
import type {
  SystemSettingsResponseApi,
  UpdateSystemSettingsRequestApi,
} from "@/api/models/systemSettings";
import { getAllCurrencies } from "@/api/services/currencyService";
import {
  getSystemSettings,
  updateSystemSettings,
} from "@/api/services/systemSettingsService";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApp } from "@/contexts/AppContext";
import {
  type ChartHealthDisplay,
  useUserPreferenceDefaults,
} from "@/contexts/UserPreferenceDefaultsContext";
import { useTerminology } from "@/hooks/useTerminology";
import { useMode } from "@/providers/ModeProvider";
import {
  type ForecastRefreshSchedule,
  ForecastRefreshScheduleModal,
  generateCronExpression,
  parseScheduleFromSettings,
} from "./ForecastRefreshScheduleModal";

const DEFAULT_CURRENCIES: CurrencyApi[] = [
  { currencyCode: "GBP", currencyName: "British Pound", currencySymbol: "£" },
  { currencyCode: "EUR", currencyName: "Euro", currencySymbol: "€" },
  { currencyCode: "USD", currencyName: "US Dollar", currencySymbol: "$" },
];

const COMPANY_NAME_MAX_LENGTH = 255;

export function GeneralSettings() {
  const { getForecastTerm } = useTerminology();
  const { mode } = useApp();
  const { isDemo } = useMode();
  const { defaults, updateDefaults } = useUserPreferenceDefaults();

  // Chart Display settings — wired to UserPreferenceDefaults
  const [healthyThreshold, setHealthyThreshold] = useState(
    String(defaults.healthyThreshold / 1000),
  );
  const [cautionThreshold, setCautionThreshold] = useState(
    String(defaults.cautionThreshold / 1000),
  );
  const [minCashThreshold, setMinCashThreshold] = useState(
    String(defaults.minCashThreshold / 1000),
  );
  const [chartDisplayDashboard, setChartDisplayDashboard] =
    useState<ChartHealthDisplay>(defaults.chartHealthDashboard90);
  const [chartDisplayForecast90, setChartDisplayForecast90] =
    useState<ChartHealthDisplay>(defaults.chartHealthForecast90);
  const [savingChartDisplay, setSavingChartDisplay] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [originalSettings, setOriginalSettings] =
    useState<SystemSettingsResponseApi | null>(null);

  const [companyName, setCompanyName] = useState("BuildRight Construction Ltd");
  const [reportingCurrency, setReportingCurrency] = useState("GBP");

  const [currencies, setCurrencies] =
    useState<CurrencyApi[]>(DEFAULT_CURRENCIES);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [refreshSchedule, setRefreshSchedule] =
    useState<ForecastRefreshSchedule>({
      enabled: true,
      daysOfWeek: [1, 2, 3, 4, 5],
      startHour: 6,
      endHour: 18,
      interval: "daily",
    });

  const [emailNotifications, setEmailNotifications] = useState(true);
  const [weeklyReports, setWeeklyReports] = useState(true);
  const [alertThreshold, setAlertThreshold] = useState(true);
  const [systemUpdates, setSystemUpdates] = useState(false);

  const [automaticSnapshotMonthEnd, setAutomaticSnapshotMonthEnd] =
    useState(true);

  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingForecasts, setSavingForecasts] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [savingPaymentBehaviour, setSavingPaymentBehaviour] = useState(false);
  const [savingSnapshots, setSavingSnapshots] = useState(false);

  // Snapshot settings
  const [autoSnapshotHorizon, setAutoSnapshotHorizon] = useState("90d");
  const [monthEndSnapshotsEnabled, setMonthEndSnapshotsEnabled] =
    useState(true);

  // Payment Behaviour defaults
  const [defaultCustomerDays, setDefaultCustomerDays] = useState("35");
  const [defaultSupplierDays, setDefaultSupplierDays] = useState("30");
  const [defaultSalesOrderDays, setDefaultSalesOrderDays] = useState("30");
  const [defaultPurchaseOrderDays, setDefaultPurchaseOrderDays] =
    useState("30");
  const [anticipatedDatePriority, setAnticipatedDatePriority] = useState(true);
  const [learnPaymentBehaviour, setLearnPaymentBehaviour] = useState(true);
  const [learnPaymentMethod, setLearnPaymentMethod] = useState(true);

  const [companyNameError, setCompanyNameError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    if (mode !== "live") return;

    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const settings = await getSystemSettings();
      setOriginalSettings(settings);

      setCompanyName(settings.companyName || "");
      setReportingCurrency(settings.primaryCurrency?.code || "GBP");

      setEmailNotifications(settings.notificationEmailEnabled);
      setWeeklyReports(settings.notificationEmailWeeklySummary);
      setAlertThreshold(settings.notificationEmailAlertThreshold);
      setSystemUpdates(settings.notificationEmailSystemUpdates);

      setAutomaticSnapshotMonthEnd(settings.automaticSnapshotMonthEndEnabled);
      setMonthEndSnapshotsEnabled(settings.automaticSnapshotMonthEndEnabled);
      if (settings.automaticSnapshotHorizon) {
        setAutoSnapshotHorizon(settings.automaticSnapshotHorizon);
      }

      if (settings.forecastSchedule) {
        setRefreshSchedule(
          parseScheduleFromSettings(settings.forecastSchedule),
        );
      }

      if (settings.paymentBehaviourSettings) {
        setDefaultCustomerDays(
          String(settings.paymentBehaviourSettings.defaultCustomerDays),
        );
        setDefaultSupplierDays(
          String(settings.paymentBehaviourSettings.defaultSupplierDays),
        );
        setDefaultSalesOrderDays(
          String(settings.paymentBehaviourSettings.defaultSalesOrderDays),
        );
        setDefaultPurchaseOrderDays(
          String(settings.paymentBehaviourSettings.defaultPurchaseOrderDays),
        );
        setAnticipatedDatePriority(
          settings.paymentBehaviourSettings.anticipatedDatePriority,
        );
        setLearnPaymentBehaviour(settings.paymentBehaviourSettings.learnTiming);
        setLearnPaymentMethod(settings.paymentBehaviourSettings.learnMethod);
      }

      if (settings.chartDisplaySettings) {
        const chart = settings.chartDisplaySettings;
        setHealthyThreshold(String(chart.healthyThreshold / 1000));
        setCautionThreshold(String(chart.cautionThreshold / 1000));
        setMinCashThreshold(String(chart.minCashThreshold / 1000));
        setChartDisplayDashboard(
          chart.chartHealthDashboard90 as ChartHealthDisplay,
        );
        setChartDisplayForecast90(
          chart.chartHealthForecast90 as ChartHealthDisplay,
        );
        updateDefaults({
          healthyThreshold: chart.healthyThreshold,
          cautionThreshold: chart.cautionThreshold,
          minCashThreshold: chart.minCashThreshold,
          chartHealthDashboard90:
            chart.chartHealthDashboard90 as ChartHealthDisplay,
          chartHealthForecast90:
            chart.chartHealthForecast90 as ChartHealthDisplay,
        });
      }
    } catch (err) {
      setSettingsError(
        err instanceof Error ? err.message : "Failed to load settings",
      );
    } finally {
      setSettingsLoading(false);
    }
  }, [mode, updateDefaults]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      if (mode === "live") {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        try {
          const response = await getAllCurrencies();
          setCurrencies(response.currencies);
        } catch (err) {
          setCurrenciesError(
            err instanceof Error ? err.message : "Failed to load currencies",
          );
          setCurrencies(DEFAULT_CURRENCIES);
        } finally {
          setCurrenciesLoading(false);
        }
      } else {
        setCurrencies(DEFAULT_CURRENCIES);
        setCurrenciesError(null);
      }
    };

    fetchCurrencies();
    fetchSettings();
  }, [mode, fetchSettings]);

  const validateCompanyName = (name: string): boolean => {
    if (name.length > COMPANY_NAME_MAX_LENGTH) {
      setCompanyNameError(
        `Company name must be at most ${COMPANY_NAME_MAX_LENGTH} characters`,
      );
      return false;
    }
    setCompanyNameError(null);
    return true;
  };

  const handleCompanyNameChange = (value: string) => {
    setCompanyName(value);
    validateCompanyName(value);
  };

  const buildUpdateRequest = (
    overrides: Partial<UpdateSystemSettingsRequestApi>,
  ): UpdateSystemSettingsRequestApi => {
    const cronData = generateCronExpression(refreshSchedule);

    const baseRequest: UpdateSystemSettingsRequestApi = {
      companyName: originalSettings?.companyName || companyName || null,
      primaryCurrencyCode:
        originalSettings?.primaryCurrency?.code || reportingCurrency,
      defaultForecastPeriodKey: null,
      notificationEmailEnabled:
        originalSettings?.notificationEmailEnabled ?? emailNotifications,
      notificationEmailWeeklySummary:
        originalSettings?.notificationEmailWeeklySummary ?? weeklyReports,
      notificationEmailAlertThreshold:
        originalSettings?.notificationEmailAlertThreshold ?? alertThreshold,
      notificationEmailSystemUpdates:
        originalSettings?.notificationEmailSystemUpdates ?? systemUpdates,
      forecastScheduleEnabled:
        originalSettings?.forecastSchedule?.enabled ?? cronData.enabled,
      forecastScheduleCronExpression:
        originalSettings?.forecastSchedule?.cronExpression ??
        cronData.cronExpression,
      forecastScheduleTimezone:
        originalSettings?.forecastSchedule?.timezone ?? cronData.timezone,
      forecastScheduleBetweenTimeStart:
        originalSettings?.forecastSchedule?.betweenTimeStart ??
        cronData.betweenTimeStart,
      forecastScheduleBetweenTimeEnd:
        originalSettings?.forecastSchedule?.betweenTimeEnd ??
        cronData.betweenTimeEnd,
      paymentBehaviourDefaultCustomerDays:
        originalSettings?.paymentBehaviourSettings?.defaultCustomerDays ??
        Number.parseInt(defaultCustomerDays, 10),
      paymentBehaviourDefaultSupplierDays:
        originalSettings?.paymentBehaviourSettings?.defaultSupplierDays ??
        Number.parseInt(defaultSupplierDays, 10),
      paymentBehaviourDefaultSalesOrderDays:
        originalSettings?.paymentBehaviourSettings?.defaultSalesOrderDays ??
        Number.parseInt(defaultSalesOrderDays, 10),
      paymentBehaviourDefaultPurchaseOrderDays:
        originalSettings?.paymentBehaviourSettings?.defaultPurchaseOrderDays ??
        Number.parseInt(defaultPurchaseOrderDays, 10),
      paymentBehaviourAnticipatedDatePriority:
        originalSettings?.paymentBehaviourSettings?.anticipatedDatePriority ??
        anticipatedDatePriority,
      paymentBehaviourLearnTiming:
        originalSettings?.paymentBehaviourSettings?.learnTiming ??
        learnPaymentBehaviour,
      paymentBehaviourLearnMethod:
        originalSettings?.paymentBehaviourSettings?.learnMethod ??
        learnPaymentMethod,
      automaticSnapshotMonthEndEnabled:
        originalSettings?.automaticSnapshotMonthEndEnabled ??
        automaticSnapshotMonthEnd,
      automaticSnapshotHorizon:
        originalSettings?.automaticSnapshotHorizon ?? autoSnapshotHorizon,
      healthyThreshold:
        originalSettings?.chartDisplaySettings?.healthyThreshold ??
        Number.parseFloat(healthyThreshold) * 1000,
      cautionThreshold:
        originalSettings?.chartDisplaySettings?.cautionThreshold ??
        Number.parseFloat(cautionThreshold) * 1000,
      minCashThreshold:
        originalSettings?.chartDisplaySettings?.minCashThreshold ??
        Number.parseFloat(minCashThreshold) * 1000,
      chartHealthDashboard90:
        originalSettings?.chartDisplaySettings?.chartHealthDashboard90 ??
        chartDisplayDashboard,
      chartHealthForecast90:
        originalSettings?.chartDisplaySettings?.chartHealthForecast90 ??
        chartDisplayForecast90,
    };

    return { ...baseRequest, ...overrides };
  };

  const handleSaveGeneral = async () => {
    if (!validateCompanyName(companyName)) {
      toast.error("Validation Error", {
        description: companyNameError || "Please fix the errors before saving.",
      });
      return;
    }

    if (mode === "live") {
      setSavingGeneral(true);
      try {
        const request = buildUpdateRequest({
          companyName: companyName || null,
          primaryCurrencyCode: reportingCurrency,
        });
        const updatedSettings = await updateSystemSettings(request);
        setOriginalSettings(updatedSettings);
        toast.success("General Settings Saved", {
          description: "Your settings have been updated successfully.",
        });
      } catch (err) {
        toast.error("Error", {
          description:
            err instanceof Error ? err.message : "Failed to save settings",
        });
      } finally {
        setSavingGeneral(false);
      }
    } else {
      toast.success("General Settings Saved", {
        description: "Your settings have been updated successfully.",
      });
    }
  };

  const handleSaveForecastDefaults = async () => {
    if (mode === "live") {
      setSavingForecasts(true);
      try {
        const cronData = generateCronExpression(refreshSchedule);
        const request = buildUpdateRequest({
          forecastScheduleEnabled: cronData.enabled,
          forecastScheduleCronExpression: cronData.cronExpression,
          forecastScheduleTimezone: cronData.timezone,
          forecastScheduleBetweenTimeStart: cronData.betweenTimeStart,
          forecastScheduleBetweenTimeEnd: cronData.betweenTimeEnd,
          automaticSnapshotMonthEndEnabled: automaticSnapshotMonthEnd,
        });
        const updatedSettings = await updateSystemSettings(request);
        setOriginalSettings(updatedSettings);
        toast.success(`${getForecastTerm(true)} Defaults Saved`, {
          description: `Default ${getForecastTerm(false)} parameters have been updated.`,
        });
      } catch (err) {
        toast.error("Error", {
          description:
            err instanceof Error ? err.message : "Failed to save settings",
        });
      } finally {
        setSavingForecasts(false);
      }
    } else {
      toast.success(`${getForecastTerm(true)} Defaults Saved`, {
        description: `Default ${getForecastTerm(false)} parameters have been updated.`,
      });
    }
  };

  const handleSaveNotifications = async () => {
    if (mode === "live") {
      setSavingNotifications(true);
      try {
        const request = buildUpdateRequest({
          notificationEmailEnabled: emailNotifications,
          notificationEmailWeeklySummary: weeklyReports,
          notificationEmailAlertThreshold: alertThreshold,
          notificationEmailSystemUpdates: systemUpdates,
        });
        const updatedSettings = await updateSystemSettings(request);
        setOriginalSettings(updatedSettings);
        toast.success("Notification Settings Saved", {
          description: "Email notification preferences have been updated.",
        });
      } catch (err) {
        toast.error("Error", {
          description:
            err instanceof Error ? err.message : "Failed to save settings",
        });
      } finally {
        setSavingNotifications(false);
      }
    } else {
      toast.success("Notification Settings Saved", {
        description: "Email notification preferences have been updated.",
      });
    }
  };

  const handleSaveSchedule = async (schedule: ForecastRefreshSchedule) => {
    setRefreshSchedule(schedule);

    if (mode === "live") {
      setSavingSchedule(true);
      try {
        const cronData = generateCronExpression(schedule);
        const request = buildUpdateRequest({
          forecastScheduleEnabled: cronData.enabled,
          forecastScheduleCronExpression: cronData.cronExpression,
          forecastScheduleTimezone: cronData.timezone,
          forecastScheduleBetweenTimeStart: cronData.betweenTimeStart,
          forecastScheduleBetweenTimeEnd: cronData.betweenTimeEnd,
        });
        const updatedSettings = await updateSystemSettings(request);
        setOriginalSettings(updatedSettings);
        toast.success("Refresh Schedule Saved", {
          description: `${getForecastTerm(true)} refresh schedule has been saved.`,
        });
      } catch (err) {
        toast.error("Error", {
          description:
            err instanceof Error ? err.message : "Failed to save schedule",
        });
      } finally {
        setSavingSchedule(false);
      }
    } else {
      toast.success("Refresh Schedule Updated", {
        description: `${getForecastTerm(true)} refresh schedule has been updated.`,
      });
    }
  };

  const handleSavePaymentBehaviour = async () => {
    if (mode === "live") {
      setSavingPaymentBehaviour(true);
      try {
        const request = buildUpdateRequest({
          paymentBehaviourDefaultCustomerDays: Number.parseInt(
            defaultCustomerDays,
            10,
          ),
          paymentBehaviourDefaultSupplierDays: Number.parseInt(
            defaultSupplierDays,
            10,
          ),
          paymentBehaviourDefaultSalesOrderDays: Number.parseInt(
            defaultSalesOrderDays,
            10,
          ),
          paymentBehaviourDefaultPurchaseOrderDays: Number.parseInt(
            defaultPurchaseOrderDays,
            10,
          ),
          paymentBehaviourAnticipatedDatePriority: anticipatedDatePriority,
          paymentBehaviourLearnTiming: learnPaymentBehaviour,
          paymentBehaviourLearnMethod: learnPaymentMethod,
        });
        const updatedSettings = await updateSystemSettings(request);
        setOriginalSettings(updatedSettings);
        toast.success("Payment Behaviour Settings Saved", {
          description: "Default payment assumptions have been updated.",
        });
      } catch (err) {
        toast.error("Error", {
          description:
            err instanceof Error
              ? err.message
              : "Failed to save payment behaviour settings",
        });
      } finally {
        setSavingPaymentBehaviour(false);
      }
    } else {
      setSavingPaymentBehaviour(true);
      await new Promise((resolve) => setTimeout(resolve, 600));
      setSavingPaymentBehaviour(false);
      toast.success("Payment Behaviour Settings Saved", {
        description: "Default payment assumptions have been updated.",
      });
    }
  };

  const handleSaveSnapshots = async () => {
    if (mode === "live") {
      setSavingSnapshots(true);
      try {
        const request = buildUpdateRequest({
          automaticSnapshotMonthEndEnabled: monthEndSnapshotsEnabled,
          automaticSnapshotHorizon: autoSnapshotHorizon,
        });
        const updatedSettings = await updateSystemSettings(request);
        setOriginalSettings(updatedSettings);
        setAutomaticSnapshotMonthEnd(monthEndSnapshotsEnabled);
        toast.success("Snapshot Settings Saved", {
          description: "Automatic snapshot configuration has been updated.",
        });
      } catch (err) {
        toast.error("Error", {
          description:
            err instanceof Error ? err.message : "Failed to save settings",
        });
      } finally {
        setSavingSnapshots(false);
      }
    } else {
      setSavingSnapshots(true);
      await new Promise((resolve) => setTimeout(resolve, 600));
      setSavingSnapshots(false);
      toast.success("Snapshot Settings Saved", {
        description: "Automatic snapshot configuration has been updated.",
      });
    }
  };

  const handleSaveChartDisplay = async () => {
    const healthyVal = Number.parseFloat(healthyThreshold) * 1000;
    const cautionVal = Number.parseFloat(cautionThreshold) * 1000;
    const minCashVal = Number.parseFloat(minCashThreshold) * 1000;

    if (
      Number.isNaN(healthyVal) ||
      Number.isNaN(cautionVal) ||
      Number.isNaN(minCashVal)
    ) {
      toast.error("Invalid values", {
        description: "Please enter valid threshold amounts.",
      });
      return;
    }
    if (cautionVal >= healthyVal) {
      toast.error("Invalid thresholds", {
        description:
          "The Caution threshold must be lower than the Healthy threshold.",
      });
      return;
    }

    setSavingChartDisplay(true);

    if (mode === "live") {
      try {
        const request = buildUpdateRequest({
          healthyThreshold: healthyVal,
          cautionThreshold: cautionVal,
          minCashThreshold: minCashVal,
          chartHealthDashboard90: chartDisplayDashboard,
          chartHealthForecast90: chartDisplayForecast90,
        });
        const updatedSettings = await updateSystemSettings(request);
        setOriginalSettings(updatedSettings);
        updateDefaults({
          healthyThreshold: healthyVal,
          cautionThreshold: cautionVal,
          minCashThreshold: minCashVal,
          chartHealthDashboard90: chartDisplayDashboard,
          chartHealthForecast90: chartDisplayForecast90,
        });
        toast.success("Chart Display Settings Saved", {
          description:
            "All charts will now reflect the updated thresholds and display modes.",
        });
      } catch (err) {
        toast.error("Error", {
          description:
            err instanceof Error
              ? err.message
              : "Failed to save chart display settings",
        });
      } finally {
        setSavingChartDisplay(false);
      }
      return;
    }

    updateDefaults({
      healthyThreshold: healthyVal,
      cautionThreshold: cautionVal,
      minCashThreshold: minCashVal,
      chartHealthDashboard90: chartDisplayDashboard,
      chartHealthForecast90: chartDisplayForecast90,
    });
    await new Promise((resolve) => setTimeout(resolve, 500));
    setSavingChartDisplay(false);
    toast.success("Chart Display Settings Saved", {
      description:
        "All charts will now reflect the updated thresholds and display modes.",
    });
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading settings...</span>
      </div>
    );
  }

  if (settingsError && mode === "live") {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">
              Error Loading Settings
            </CardTitle>
            <CardDescription>{settingsError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={fetchSettings}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList
          className={`grid w-full ${isDemo ? "grid-cols-6" : "grid-cols-4"}`}
        >
          <TabsTrigger value="general">
            <Settings className="h-4 w-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="forecasts">
            <TrendingUp className="h-4 w-4 mr-2" />
            {getForecastTerm(true)}s
          </TabsTrigger>
          {isDemo && (
            <TabsTrigger value="snapshots">
              <Camera className="h-4 w-4 mr-2" />
              Snapshots
            </TabsTrigger>
          )}
          {isDemo && (
            <TabsTrigger value="notifications">
              <Mail className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
          )}
          <TabsTrigger value="payment-behaviour">
            <Clock className="h-4 w-4 mr-2" />
            Payment Behaviour
          </TabsTrigger>
          <TabsTrigger value="chart-display">
            <BarChart2 className="h-4 w-4 mr-2" />
            Chart Display
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>General</CardTitle>
              <CardDescription>
                Basic settings for reports and display
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">
                  Company name to use on reports and exports
                </Label>
                <Input
                  id="company-name"
                  value={companyName}
                  onChange={(e) => handleCompanyNameChange(e.target.value)}
                  maxLength={COMPANY_NAME_MAX_LENGTH}
                  className={companyNameError ? "border-destructive" : ""}
                />
                <div className="flex justify-between">
                  <p className="text-xs text-muted-foreground">
                    This name will appear on all generated reports and exports
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {companyName.length}/{COMPANY_NAME_MAX_LENGTH}
                  </p>
                </div>
                {companyNameError && (
                  <p className="text-xs text-destructive">{companyNameError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reporting-currency">
                  Currency for financial reporting
                </Label>
                <Select
                  value={reportingCurrency}
                  onValueChange={setReportingCurrency}
                  disabled={currenciesLoading}
                >
                  <SelectTrigger>
                    {currenciesLoading ? (
                      <div className="flex items-center">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span>Loading currencies...</span>
                      </div>
                    ) : (
                      <SelectValue />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem
                        key={currency.currencyCode}
                        value={currency.currencyCode}
                      >
                        {currency.currencyCode} ({currency.currencySymbol}) -{" "}
                        {currency.currencyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Primary currency for all financial displays and reports
                </p>
                {currenciesError && (
                  <p className="text-xs text-destructive">
                    {currenciesError}. Using default currencies.
                  </p>
                )}
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveGeneral} disabled={savingGeneral}>
                  {savingGeneral ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {savingGeneral ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecast Defaults Tab */}
        <TabsContent value="forecasts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{getForecastTerm(true)} Refresh Schedule</CardTitle>
              <CardDescription>
                Configure when the {getForecastTerm(true)} Builder recalculates
                predictions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <Button
                  variant="outline"
                  onClick={() => setScheduleModalOpen(true)}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Configure Schedule
                </Button>
                {originalSettings?.forecastSchedule?.description && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Schedule: {originalSettings.forecastSchedule.description}
                  </p>
                )}
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Automatic Month-End Snapshots</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically take a {getForecastTerm(false)} snapshot at
                    the end of each month
                  </p>
                </div>
                <Switch
                  checked={automaticSnapshotMonthEnd}
                  onCheckedChange={setAutomaticSnapshotMonthEnd}
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveForecastDefaults}
                  disabled={savingForecasts}
                >
                  {savingForecasts ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {savingForecasts
                    ? "Saving..."
                    : `Save ${getForecastTerm(true)} Defaults`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Snapshots Tab — demo only */}
        {isDemo && (
          <TabsContent value="snapshots" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Automatic Snapshot Horizon</CardTitle>
                <CardDescription>
                  How far forward automatic snapshots project when they are
                  taken. This determines how much future data is available for
                  variance analysis later.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="snapshot-horizon">Snapshot horizon</Label>
                  <Select
                    value={autoSnapshotHorizon}
                    onValueChange={setAutoSnapshotHorizon}
                  >
                    <SelectTrigger id="snapshot-horizon" className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="90d">90 Days</SelectItem>
                      <SelectItem value="6m">6 Months</SelectItem>
                      <SelectItem value="12m">12 Months</SelectItem>
                      <SelectItem value="3y">3 Years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 px-4 py-3">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                      Automatic snapshots
                    </p>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      A snapshot is taken automatically every day at 00:05 UTC.
                      Manual snapshots can also be taken at any time from the
                      Variance Analysis page by users with the appropriate
                      permission. The 30 most recent snapshots — automatic and
                      manual combined — are retained. Older snapshots are
                      discarded automatically.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSaveSnapshots}
                    disabled={savingSnapshots}
                  >
                    {savingSnapshots ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {savingSnapshots ? "Saving..." : "Save Snapshot Settings"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Notifications Tab — demo only */}
        {isDemo && (
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Email Notification Settings</CardTitle>
                <CardDescription>
                  Configure system-wide email notifications and alerts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Enable Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Master switch for all email notifications
                    </p>
                  </div>
                  <Switch
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Weekly Summary Reports</Label>
                    <p className="text-sm text-muted-foreground">
                      Send weekly {getForecastTerm(false)} summaries to all
                      users
                    </p>
                  </div>
                  <Switch
                    checked={weeklyReports}
                    onCheckedChange={setWeeklyReports}
                    disabled={!emailNotifications}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Alert Threshold Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Notify users when cash flow falls below thresholds
                    </p>
                  </div>
                  <Switch
                    checked={alertThreshold}
                    onCheckedChange={setAlertThreshold}
                    disabled={!emailNotifications}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>System Update Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Notify administrators about system updates
                    </p>
                  </div>
                  <Switch
                    checked={systemUpdates}
                    onCheckedChange={setSystemUpdates}
                    disabled={!emailNotifications}
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSaveNotifications}
                    disabled={savingNotifications}
                  >
                    {savingNotifications ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {savingNotifications
                      ? "Saving..."
                      : "Save Notification Settings"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Payment Behaviour Tab */}
        <TabsContent value="payment-behaviour" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Payment Behaviour Defaults</CardTitle>
              <CardDescription>
                Set default assumptions for how quickly new counterparties are
                expected to pay or be paid. These defaults apply only when no
                payment history exists and no anticipated payment date has been
                recorded. The system will learn actual behaviour over time and
                replace these defaults automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Priority stack explanation */}
              <div className="rounded-lg bg-muted/50 border p-4 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  How payment date is determined
                </p>
                <ol className="text-sm text-muted-foreground space-y-1 list-none">
                  {[
                    {
                      step: "1",
                      label: "Due date",
                      desc: "If the transaction due date is later than the anticipated payment date, it takes priority",
                    },
                    {
                      step: "2",
                      label: "Anticipated payment date",
                      desc: "Manually recorded by credit controller — takes priority unless due date is later",
                      highlight: true,
                    },
                    {
                      step: "3",
                      label: "Learned counterparty behaviour",
                      desc: "Based on actual payment history for this specific counterparty",
                    },
                    {
                      step: "4",
                      label: "Learned category average",
                      desc: "Average behaviour across similar counterparty types",
                    },
                    {
                      step: "5",
                      label: "Organisation default (configured below)",
                      desc: "Your settings on this page — used when no history exists",
                    },
                    {
                      step: "6",
                      label: "Transaction date",
                      desc: "Last resort fallback if nothing else is available",
                    },
                  ].map(({ step, label, desc, highlight }) => (
                    <li key={step} className="flex items-start gap-3">
                      <span
                        className={`flex-shrink-0 w-5 h-5 rounded-full text-xs flex items-center justify-center font-medium mt-0.5 ${highlight ? "bg-primary text-primary-foreground" : "bg-muted-foreground/20 text-muted-foreground"}`}
                      >
                        {step}
                      </span>
                      <span>
                        <span
                          className={
                            highlight ? "font-medium text-foreground" : ""
                          }
                        >
                          {label}
                        </span>
                        <span className="text-muted-foreground"> — {desc}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <Separator />

              {/* Behaviour learning toggles */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Automatic Learning</h3>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Prioritise anticipated payment dates</Label>
                    <p className="text-sm text-muted-foreground">
                      When a credit controller records an anticipated date in
                      the finance system, use it unless the transaction due date
                      is later
                    </p>
                  </div>
                  <Switch
                    checked={anticipatedDatePriority}
                    onCheckedChange={setAnticipatedDatePriority}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Learn payment timing from history</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically build payment behaviour profiles per
                      counterparty based on their actual payment patterns
                    </p>
                  </div>
                  <Switch
                    checked={learnPaymentBehaviour}
                    onCheckedChange={setLearnPaymentBehaviour}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Learn payment method from history</Label>
                    <p className="text-sm text-muted-foreground">
                      Track whether counterparties pay by BACS, Faster Payments,
                      cheque, or card — each carries different settlement timing
                    </p>
                  </div>
                  <Switch
                    checked={learnPaymentMethod}
                    onCheckedChange={setLearnPaymentMethod}
                    disabled={!learnPaymentBehaviour}
                  />
                </div>
              </div>

              <Separator />

              {/* Default days by category */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium">
                    Default Payment Timing by Transaction Type
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    These values are used for new counterparties with no payment
                    history. Enter the typical number of days to cash receipt or
                    payment. These will be replaced by learned behaviour once
                    enough history is available.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="default-customer-days">
                      Customers (sales invoices)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="default-customer-days"
                        type="number"
                        min="0"
                        max="365"
                        value={defaultCustomerDays}
                        onChange={(e) => setDefaultCustomerDays(e.target.value)}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">
                        days from invoice date
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      UK SME average: ~35 days
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default-supplier-days">
                      Suppliers (purchase invoices)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="default-supplier-days"
                        type="number"
                        min="0"
                        max="365"
                        value={defaultSupplierDays}
                        onChange={(e) => setDefaultSupplierDays(e.target.value)}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">
                        days from invoice date
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your typical payment cycle from invoice to payment
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default-sales-order-days">
                      Sales orders
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="default-sales-order-days"
                        type="number"
                        min="0"
                        max="365"
                        value={defaultSalesOrderDays}
                        onChange={(e) =>
                          setDefaultSalesOrderDays(e.target.value)
                        }
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">
                        days from sales order to cash receipt
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Typical days from sales order to cash receipt
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default-purchase-order-days">
                      Purchase orders
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="default-purchase-order-days"
                        type="number"
                        min="0"
                        max="365"
                        value={defaultPurchaseOrderDays}
                        onChange={(e) =>
                          setDefaultPurchaseOrderDays(e.target.value)
                        }
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">
                        days from purchase order to payment
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Typical days from purchase order to payment
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Note:</span> These are
                  organisation-wide defaults. Individual counterparty behaviour
                  learned from history will always take precedence once
                  sufficient data is available (typically 3+ transactions).
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSavePaymentBehaviour}
                  disabled={savingPaymentBehaviour}
                >
                  {savingPaymentBehaviour ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {savingPaymentBehaviour
                    ? "Saving..."
                    : "Save Payment Behaviour Settings"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Chart Display Tab */}
        <TabsContent value="chart-display" className="space-y-6">
          {/* Health Zone Thresholds */}
          <Card>
            <CardHeader>
              <CardTitle>Health Zone Thresholds</CardTitle>
              <CardDescription>
                Define the cash balance boundaries that determine the colour
                zones on charts. These apply to the Dashboard, 90-Day, and
                Scenario Overlay charts.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Zone preview */}
              <div className="rounded-lg border overflow-hidden">
                <div className="flex h-8">
                  <div className="flex-1 bg-green-100 flex items-center justify-center text-xs font-medium text-green-800">
                    🟢 Healthy — above £{healthyThreshold || "100"}K
                  </div>
                </div>
                <div className="flex h-8">
                  <div className="flex-1 bg-amber-100 flex items-center justify-center text-xs font-medium text-amber-800">
                    🟡 Caution — £{cautionThreshold || "50"}K to £
                    {healthyThreshold || "100"}K
                  </div>
                </div>
                <div className="flex h-8">
                  <div className="flex-1 bg-red-100 flex items-center justify-center text-xs font-medium text-red-800">
                    🔴 Critical — below £{cautionThreshold || "50"}K
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="healthyThreshold">
                    Healthy threshold (£K)
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">£</span>
                    <Input
                      id="healthyThreshold"
                      type="number"
                      min="0"
                      value={healthyThreshold}
                      onChange={(e) => setHealthyThreshold(e.target.value)}
                      className="w-32"
                    />
                    <span className="text-muted-foreground text-sm">K</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Cash above this amount = green zone
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cautionThreshold">
                    Caution threshold (£K)
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">£</span>
                    <Input
                      id="cautionThreshold"
                      type="number"
                      min="0"
                      value={cautionThreshold}
                      onChange={(e) => setCautionThreshold(e.target.value)}
                      className="w-32"
                    />
                    <span className="text-muted-foreground text-sm">K</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Cash below Healthy but above this = amber zone. Below this =
                    red zone.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Min Cash Threshold Line */}
          <Card>
            <CardHeader>
              <CardTitle>Minimum Cash Threshold Line</CardTitle>
              <CardDescription>
                A separate dashed reference line shown on all charts. This is
                your absolute floor — the minimum cash balance the business must
                maintain. Independent of the health zone colours.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="minCashThreshold">
                Minimum cash threshold (£K)
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">£</span>
                <Input
                  id="minCashThreshold"
                  type="number"
                  min="0"
                  value={minCashThreshold}
                  onChange={(e) => setMinCashThreshold(e.target.value)}
                  className="w-32"
                />
                <span className="text-muted-foreground text-sm">K</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Shown as a dashed red line across all charts. Alerts trigger
                when the forecast falls below this value.
              </p>
            </CardContent>
          </Card>

          {/* Per-chart display mode */}
          <Card>
            <CardHeader>
              <CardTitle>Chart Display Mode</CardTitle>
              <CardDescription>
                Control how health zones are displayed on each chart. These are
                system-wide defaults for all users.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {(
                [
                  {
                    label: "Dashboard — 90-Day Card",
                    value: chartDisplayDashboard,
                    setter: setChartDisplayDashboard,
                  },
                  {
                    label: "90-Day Forecast",
                    value: chartDisplayForecast90,
                    setter: setChartDisplayForecast90,
                  },
                ] as const
              ).map(({ label, value, setter }) => (
                <div key={label} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-muted-foreground">
                      {value === "off"
                        ? "No health zones or threshold line shown"
                        : value === "line"
                          ? "Threshold line only — no coloured zone backgrounds"
                          : "Full coloured zone bands + threshold line"}
                    </p>
                  </div>
                  <div className="flex rounded-md border overflow-hidden">
                    {(["off", "line", "bands"] as ChartHealthDisplay[]).map(
                      (option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setter(option as ChartHealthDisplay)}
                          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                            value === option
                              ? "bg-primary text-primary-foreground"
                              : "bg-background text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          {option === "off"
                            ? "Off"
                            : option === "line"
                              ? "Line only"
                              : "Full bands"}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              onClick={handleSaveChartDisplay}
              disabled={savingChartDisplay}
            >
              {savingChartDisplay ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {savingChartDisplay ? "Saving..." : "Save Chart Display Settings"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <ForecastRefreshScheduleModal
        open={scheduleModalOpen}
        onOpenChange={setScheduleModalOpen}
        schedule={refreshSchedule}
        onSave={handleSaveSchedule}
      />
    </div>
  );
}
